import { useCallback, useEffect } from 'react';
import { trackButtonClick as fbTrackButtonClick, trackSectionView as fbTrackSectionView } from '../firebaseConfig';
import { trackGAButtonClick, trackGASectionView, trackGAPageView, trackGAConversion } from '../lib/googleAnalytics';

// User data interface
interface UserData {
  gender?: string;
  location?: string;
  age?: string;
  language?: string;
  timezone?: string;
}

// Extract user data from cookies and browser
const getUserData = (): UserData => {
  const userData: UserData = {};
  
  try {
    // Extract from cookies
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      switch (name) {
        case 'user_gender':
        case 'gender':
          userData.gender = decodeURIComponent(value);
          break;
        case 'user_location':
        case 'location':
        case 'country':
          userData.location = decodeURIComponent(value);
          break;
        case 'user_age':
        case 'age':
          userData.age = decodeURIComponent(value);
          break;
      }
    });

    // Extract from localStorage
    const storedGender = localStorage.getItem('user_gender');
    const storedLocation = localStorage.getItem('user_location');
    const storedAge = localStorage.getItem('user_age');
    
    if (storedGender && !userData.gender) userData.gender = storedGender;
    if (storedLocation && !userData.location) userData.location = storedLocation;
    if (storedAge && !userData.age) userData.age = storedAge;

    // Extract from browser
    if (!userData.language) {
      userData.language = navigator.language || navigator.languages?.[0];
    }
    userData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Error extracting user data:', error);
  }

  return userData;
};

// Main analytics tracking hook
export const useAnalyticsTracking = () => {
  const userData = getUserData();

  // Track button clicks
  const trackButtonClick = useCallback((buttonName: string, location: string, additionalParams: Record<string, any> = {}) => {
    const params = { ...additionalParams, ...userData };

    try {
      // Firebase Analytics (existing)
      fbTrackButtonClick(buttonName, location);

      // Google Analytics
      trackGAButtonClick(buttonName, location, params);

      // Meta Pixel - Enhanced tracking
      if (window.fbq) {
        // Determine the appropriate Meta Pixel event based on button type
        let metaEvent = null;
        let useStandardEvent = false;
        
        if (buttonName.toLowerCase().includes('sign up') || buttonName.toLowerCase().includes('try')) {
          metaEvent = 'Lead';
          useStandardEvent = true;
        } else if (buttonName.toLowerCase().includes('pricing')) {
          metaEvent = 'ViewContent';
          useStandardEvent = true;
        } else if (buttonName.toLowerCase().includes('demo')) {
          metaEvent = 'InitiateCheckout';
          useStandardEvent = true;
        }

        const eventParams = {
          button_name: buttonName,
          button_location: location,
          content_name: buttonName,
          content_category: 'button_click',
          ...params
        };

        if (useStandardEvent && metaEvent) {
          // Use standard Meta Pixel event
          window.fbq('track', metaEvent, eventParams);
        } else {
          // Use custom event for generic button clicks
          window.fbq('trackCustom', 'ButtonClick', {
            ...eventParams,
            event_type: 'generic_button_click'
          });
        }

        // Always track a detailed custom event for analytics
        window.fbq('trackCustom', 'DetailedButtonClick', {
          button_name: buttonName,
          button_location: location,
          ...params
        });
      }

      console.log(`Button tracked: ${buttonName} at ${location}`, params);
    } catch (error) {
      console.warn('Error tracking button click:', error);
    }
  }, [userData]);

  // Track section views
  const trackSectionView = useCallback((sectionName: string, additionalParams: Record<string, any> = {}) => {
    const params = { ...additionalParams, ...userData };

    // Firebase Analytics (existing)
    fbTrackSectionView(sectionName);

    // Google Analytics
    trackGASectionView(sectionName, params);

    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: sectionName,
        content_type: 'section',
        ...params
      });
    }

    console.log(`Section tracked: ${sectionName}`, params);
  }, [userData]);

  // Track page views
  const trackPageView = useCallback((pageName: string, pageUrl?: string, additionalParams: Record<string, any> = {}) => {
    const params = { ...additionalParams, ...userData };

    // Google Analytics
    trackGAPageView(pageName, {
      page_url: pageUrl || window.location.href,
      ...params
    });

    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'PageView', {
        page_name: pageName,
        page_url: pageUrl || window.location.href,
        ...params
      });
    }

    console.log(`Page tracked: ${pageName}`, params);
  }, [userData]);

  // Track conversions
  const trackConversion = useCallback((conversionType: 'sign_up' | 'trial_request' | 'pricing_view' | 'demo_request' | 'weekend_free_signup' | 'extension_download', additionalParams: Record<string, any> = {}) => {
    const params = { ...additionalParams, ...userData };

    try {
      // Google Analytics
      trackGAConversion(conversionType, params);

      // Meta Pixel - Enhanced conversion tracking
      if (window.fbq) {
        let metaEvent = 'Lead';
        let eventParams = {
          conversion_type: conversionType,
          content_name: conversionType,
          content_category: 'conversion',
          registration_method: 'website',
          lead_type: conversionType,
          content_type: 'pricing',
          ...params
        };

        switch (conversionType) {
          case 'sign_up':
            metaEvent = 'CompleteRegistration';
            eventParams.registration_method = 'website';
            break;
          case 'trial_request':
            metaEvent = 'Lead';
            eventParams.lead_type = 'trial_request';
            break;
          case 'pricing_view':
            metaEvent = 'ViewContent';
            eventParams.content_type = 'pricing';
            break;
          case 'demo_request':
            metaEvent = 'Lead';
            eventParams.lead_type = 'demo_request';
            break;
        }
        
        window.fbq('track', metaEvent, eventParams);

        // Also track custom conversion event
        window.fbq('trackCustom', 'Conversion', {
          conversion_type: conversionType,
          ...params
        });
      }

      console.log(`Conversion tracked: ${conversionType}`, params);
    } catch (error) {
      console.warn('Error tracking conversion:', error);
    }
  }, [userData]);

  // Track video interactions
  const trackVideoInteraction = useCallback((action: 'play' | 'pause' | 'complete', videoName: string, additionalParams: Record<string, any> = {}) => {
    const params = { 
      video_action: action,
      video_name: videoName,
      ...additionalParams, 
      ...userData 
    };

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', `video_${action}`, {
        event_category: 'video',
        event_label: videoName,
        ...params
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'VideoInteraction', params);
    }

    console.log(`Video tracked: ${action} - ${videoName}`, params);
  }, [userData]);

  // Track scroll depth
  const trackScrollDepth = useCallback((percentage: number, additionalParams: Record<string, any> = {}) => {
    const params = { 
      scroll_percentage: percentage,
      ...additionalParams, 
      ...userData 
    };

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'scroll', {
        event_category: 'engagement',
        event_label: `${percentage}%`,
        value: percentage,
        ...params
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'ScrollDepth', params);
    }

    console.log(`Scroll depth tracked: ${percentage}%`, params);
  }, [userData]);

  // Track time on page
  const trackTimeOnPage = useCallback((seconds: number, additionalParams: Record<string, any> = {}) => {
    const params = { 
      time_on_page: seconds,
      ...additionalParams, 
      ...userData 
    };

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'timing_complete', {
        event_category: 'engagement',
        event_label: 'time_on_page',
        value: seconds,
        ...params
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'TimeOnPage', params);
    }

    console.log(`Time on page tracked: ${seconds}s`, params);
  }, [userData]);

  // Track navigation
  const trackNavigation = useCallback((destination: string, additionalParams: Record<string, any> = {}) => {
    const params = { 
      navigation_destination: destination,
      ...additionalParams, 
      ...userData 
    };

    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'navigate', {
        event_category: 'navigation',
        event_label: destination,
        ...params
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'Navigation', params);
    }

    console.log(`Navigation tracked: ${destination}`, params);
  }, [userData]);

  return {
    trackButtonClick,
    trackSectionView,
    trackPageView,
    trackConversion,
    trackVideoInteraction,
    trackScrollDepth,
    trackTimeOnPage,
    trackNavigation,
    userData
  };
};

// Hook for button tracking with section context
export const useButtonTracking = (section: string) => {
  const { trackButtonClick } = useAnalyticsTracking();
  
  return useCallback((buttonName: string, additionalParams?: Record<string, any>) => {
    trackButtonClick(buttonName, section, additionalParams);
  }, [trackButtonClick, section]);
};

// Hook for section tracking
export const useSectionTracking = () => {
  const { trackSectionView } = useAnalyticsTracking();
  
  return useCallback((sectionName: string, additionalParams?: Record<string, any>) => {
    trackSectionView(sectionName, additionalParams);
  }, [trackSectionView]);
};

// Hook for page tracking
export const usePageTracking = () => {
  const { trackPageView } = useAnalyticsTracking();
  
  return useCallback((pageName: string, pageUrl?: string, additionalParams?: Record<string, any>) => {
    trackPageView(pageName, pageUrl, additionalParams);
  }, [trackPageView]);
};

// Hook for conversion tracking
export const useConversionTracking = () => {
  const { trackConversion } = useAnalyticsTracking();
  
  return useCallback((conversionType: 'sign_up' | 'trial_request' | 'pricing_view' | 'demo_request', additionalParams?: Record<string, any>) => {
    trackConversion(conversionType, additionalParams);
  }, [trackConversion]);
};

// Hook for video tracking
export const useVideoTracking = () => {
  const { trackVideoInteraction } = useAnalyticsTracking();
  
  return useCallback((action: 'play' | 'pause' | 'complete', videoName: string, additionalParams?: Record<string, any>) => {
    trackVideoInteraction(action, videoName, additionalParams);
  }, [trackVideoInteraction]);
}; 